import Download from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';

import { type Invoice, InvoiceKind } from '~/data/schemas/invoice';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

import { useStampedInvoice } from './useStampedInvoice';

export function DownloadStampedInvoiceDropdownMenu({
  invoice,
  outlined = false,
}: {
  invoice: Invoice;
  outlined?: boolean;
}) {
  const { downloadStampedInvoice, isLoading } = useStampedInvoice({ invoice });
  const { showSnackbar } = useLAISnackbar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownload = async (includeSupportingData: boolean) => {
    try {
      await downloadStampedInvoice({ includeSupportingData });
    } catch {
      showSnackbar('Failed to download stamped invoice. Please try again.', {
        variant: 'error',
      });
    } finally {
      setAnchorEl(null);
    }
  };

  return (
    <div>
      {outlined ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<Download />}
          onClick={handleClick}
          disabled={isLoading}
          sx={BUTTON_SX}
        >
          {isLoading ? 'Downloading...' : 'Download'}
        </Button>
      ) : (
        <Button onClick={handleClick} disabled={isLoading} sx={{ ...BUTTON_SX, pl: 0 }}>
          <Download sx={{ fontSize: 16, mr: 1 }} /> {isLoading ? 'Downloading...' : 'Download'}
        </Button>
      )}
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => handleDownload(false)}>
          {invoice.kind === InvoiceKind.Proposal
            ? 'Download Stamped Proposal'
            : 'Download Stamped Invoice'}
        </MenuItem>
        <MenuItem onClick={() => handleDownload(true)}>Download with Supporting Data</MenuItem>
      </Menu>
    </div>
  );
}
